/* ==========================================================================
   quiz.js — Full-screen immersive Hair Growth Assessment
   Reads product recommendations from #quiz-product-data (emitted by Liquid).
   ========================================================================== */

(() => {
  'use strict';

  const quiz = document.querySelector('[data-quiz]');
  if (!quiz) return;

  document.body.classList.add('quiz-page');

  const steps = quiz.querySelectorAll('[data-quiz-step]');
  const progressBar = quiz.querySelector('[data-quiz-progress]');
  const stepLabel = quiz.querySelector('[data-quiz-step-label]');
  const backBtn = quiz.querySelector('[data-quiz-back]');
  const resultsPanel = quiz.querySelector('[data-quiz-results]');
  const retakeBtn = quiz.querySelector('[data-quiz-retake]');
  const dots = quiz.querySelectorAll('[data-quiz-dot]');

  const TOTAL_STEPS = steps.length;
  let currentStep = 1;
  let isAnimating = false;
  const answers = {};

  // Read product recommendations from Liquid-emitted JSON.
  // Shape: { [concernKey]: { name, desc, price, img, url } }
  let products = {};
  try {
    const dataEl = document.getElementById('quiz-product-data');
    if (dataEl && dataEl.textContent.trim()) {
      products = JSON.parse(dataEl.textContent);
    }
  } catch (err) {
    console.warn('[quiz] Failed to parse #quiz-product-data:', err);
  }

  function showStep(n, direction) {
    if (isAnimating) return;
    isAnimating = true;

    const dir = direction || 'forward';
    const animClass = dir === 'forward' ? 'is-entering-forward' : 'is-entering-back';

    steps.forEach(s => {
      s.classList.remove('is-active', 'is-entering-forward', 'is-entering-back');
    });

    const target = quiz.querySelector(`[data-quiz-step="${n}"]`);
    if (target) {
      target.classList.add('is-active', animClass);
      target.addEventListener('animationend', () => {
        target.classList.remove(animClass);
        isAnimating = false;
      }, { once: true });
    } else {
      isAnimating = false;
    }

    currentStep = n;
    if (progressBar) progressBar.style.width = ((n / TOTAL_STEPS) * 100) + '%';
    if (stepLabel) stepLabel.textContent = `Question ${n} of ${TOTAL_STEPS}`;
    if (backBtn) backBtn.disabled = n === 1;

    dots.forEach(d => {
      const dn = parseInt(d.dataset.quizDot, 10);
      d.classList.remove('is-active', 'is-complete');
      if (dn === n) d.classList.add('is-active');
      else if (dn < n || answers[dn]) d.classList.add('is-complete');
    });

    if (answers[n] && target) {
      target.querySelectorAll('.quiz__option').forEach(o => {
        const match = o.dataset.value === answers[n];
        o.classList.toggle('is-selected', match);
        o.setAttribute('aria-checked', match ? 'true' : 'false');
      });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function showResults() {
    if (isAnimating) return;
    isAnimating = true;

    steps.forEach(s => s.classList.remove('is-active'));
    quiz.classList.add('is-complete');

    const concern = answers[1];
    const product = (concern && products[concern]) || products.edges || null;

    const imgEl = quiz.querySelector('[data-quiz-result-img]');
    const nameEl = quiz.querySelector('[data-quiz-result-name]');
    const descEl = quiz.querySelector('[data-quiz-result-desc]');
    const priceEl = quiz.querySelector('[data-quiz-result-price]');
    const urlEl = quiz.querySelector('[data-quiz-result-url]');

    if (product) {
      if (imgEl) { imgEl.src = product.img || ''; imgEl.alt = product.name || ''; }
      if (nameEl) nameEl.textContent = product.name || '';
      if (descEl) descEl.textContent = product.desc || '';
      if (priceEl) priceEl.textContent = product.price || '';
      if (urlEl && product.url) urlEl.setAttribute('href', product.url);
    } else {
      // No recommendations configured — degrade gracefully.
      if (nameEl) nameEl.textContent = 'Explore Our Collection';
      if (descEl) descEl.textContent = 'We\'ll help you find the right formula for your hair.';
      if (urlEl) urlEl.setAttribute('href', '/collections/all');
    }

    if (resultsPanel) {
      resultsPanel.classList.add('is-active');
      resultsPanel.addEventListener('animationend', () => {
        isAnimating = false;
      }, { once: true });
    } else {
      isAnimating = false;
    }

    dots.forEach(d => {
      d.classList.remove('is-active');
      d.classList.add('is-complete');
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function reset() {
    Object.keys(answers).forEach(k => delete answers[k]);
    quiz.classList.remove('is-complete');
    if (resultsPanel) resultsPanel.classList.remove('is-active');
    quiz.querySelectorAll('.quiz__option').forEach(o => {
      o.classList.remove('is-selected');
      o.setAttribute('aria-checked', 'false');
    });
    isAnimating = false;
    showStep(1, 'back');
  }

  quiz.addEventListener('click', (e) => {
    const option = e.target.closest('.quiz__option');
    if (!option || isAnimating) return;

    const step = option.closest('[data-quiz-step]');
    if (!step) return;
    const stepNum = parseInt(step.dataset.quizStep, 10);

    step.querySelectorAll('.quiz__option').forEach(o => {
      o.classList.remove('is-selected');
      o.setAttribute('aria-checked', 'false');
    });
    option.classList.add('is-selected');
    option.setAttribute('aria-checked', 'true');
    answers[stepNum] = option.dataset.value;

    setTimeout(() => {
      if (stepNum < TOTAL_STEPS) {
        showStep(stepNum + 1, 'forward');
      } else {
        showResults();
      }
    }, 350);
  });

  if (backBtn) {
    backBtn.addEventListener('click', () => {
      if (currentStep > 1 && !isAnimating) showStep(currentStep - 1, 'back');
    });
  }

  if (retakeBtn) retakeBtn.addEventListener('click', reset);

  dots.forEach(d => {
    d.addEventListener('click', () => {
      const dn = parseInt(d.dataset.quizDot, 10);
      if (dn <= currentStep && !isAnimating) {
        showStep(dn, dn < currentStep ? 'back' : 'forward');
      }
    });
  });

  quiz.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      const next = document.activeElement?.nextElementSibling;
      if (next?.classList.contains('quiz__option')) { e.preventDefault(); next.focus(); }
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      const prev = document.activeElement?.previousElementSibling;
      if (prev?.classList.contains('quiz__option')) { e.preventDefault(); prev.focus(); }
    }
  });

  showStep(1, 'forward');
})();
